<template>
  <div
    ref="rootEl"
    class="absolute inset-0 bg-background z-50 flex flex-col"
    tabindex="0"
    @keydown="emit('keydown', $event)"
  >
    <slot name="header" />

    <div class="relative flex-1 min-h-0 flex flex-col">
      <div
        ref="containerEl"
        class="flex-1 overflow-scroll relative select-none sheet-sections-scroll"
        @click.self="emit('container-click')"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 flex items-center justify-center"
        >
          <div class="text-center">
            <div class="w-10 h-10 border-4 border-t-primary border-surface rounded-full animate-spin mb-3 mx-auto" />
            <p class="text-text-secondary text-sm">
              Loading sheet music…
            </p>
          </div>
        </div>

        <div
          ref="osmdEl"
          class="w-full"
        />

        <template v-if="!isLoading && !readOnly">
          <div
            v-for="mb in measureBounds"
            :key="mb.measureIndex"
            class="absolute transition-colors hover:bg-white/5"
            :class="editable ? 'cursor-crosshair touch-pan-y' : 'cursor-pointer'"
            :style="measureStyle(mb)"
            @pointerdown="editable ? startDragCreatePointer(mb.measureIndex, $event) : undefined"
          />

          <template
            v-for="section in sections"
            :key="section.id"
          >
            <div
              v-for="(seg, si) in getSectionSegments(section)"
              :key="`${section.id}-${si}`"
              class="absolute rounded-sm transition-all"
              :class="{ 'ring-2 ring-white/60': section.id === selectedId, 'touch-pan-y': editable }"
              :style="segmentStyle(seg, section)"
              @dblclick.stop="emit('section-activate', section, $event)"
              @contextmenu.stop.prevent="emit('section-activate', section, $event)"
              @pointerdown.stop="startSectionPointer(section, $event)"
            >
              <div
                v-if="seg.isFirst && editable"
                class="absolute left-0 top-0 bottom-0 w-5 cursor-ew-resize flex items-center justify-center z-10 rounded-l-sm bg-black/25 touch-none"
                title="Drag to move section start"
                @pointerdown.stop.prevent="startDragEdgePointer(section, 'head', $event)"
              >
                <div class="w-0.5 h-5 rounded-full bg-white/70" />
              </div>

              <div
                v-if="seg.isFirst"
                class="absolute top-1 text-xs font-semibold text-white pointer-events-none overflow-hidden whitespace-nowrap"
                :style="sectionLabelStyle(seg)"
              >
                <slot
                  name="section-label"
                  :section="section"
                >
                  {{ section.name }}
                </slot>
              </div>

              <div
                v-if="seg.isLast && editable"
                class="absolute right-0 top-0 bottom-0 w-5 cursor-ew-resize flex items-center justify-center z-10 rounded-r-sm bg-black/25 touch-none"
                title="Drag to move section end"
                @pointerdown.stop.prevent="startDragEdgePointer(section, 'tail', $event)"
              >
                <div class="w-0.5 h-5 rounded-full bg-white/70" />
              </div>
            </div>
          </template>
        </template>

        <slot name="overlay" />
      </div>

      <div
        v-if="!isLoading"
        class="absolute bottom-4 right-4 z-40 flex flex-col items-stretch overflow-hidden rounded-xl border border-surface-hover bg-secondary/90 shadow-lg backdrop-blur select-none"
      >
        <button
          type="button"
          class="flex h-10 w-10 items-center justify-center text-text-primary transition-colors hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-40"
          aria-label="Zoom in"
          title="Zoom in"
          :disabled="!canZoomIn"
          @click="zoomIn"
        >
          <MagnifyingGlassPlusIcon class="h-5 w-5" />
        </button>
        <div class="flex h-7 items-center justify-center border-y border-surface-hover text-[11px] font-medium tabular-nums text-text-secondary">
          {{ zoomPercent }}%
        </div>
        <button
          type="button"
          class="flex h-10 w-10 items-center justify-center text-text-primary transition-colors hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-40"
          aria-label="Zoom out"
          title="Zoom out"
          :disabled="!canZoomOut"
          @click="zoomOut"
        >
          <MagnifyingGlassMinusIcon class="h-5 w-5" />
        </button>
      </div>
    </div>

    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/vue/24/outline';
import { readMusicXmlFile, hexToRgba, computeOsmdMeasureBounds, getMeasureAtPoint, getSectionSegments as computeSectionSegments, recolorOsmdByTheme, roundToTwoDecimals } from '@/utils';
import { toLogicalPoint } from '@/utils/screenRotation';
import type { MeasureBound, PieceSection, SectionSegment } from '@/types';
import { useSettings } from '@/composables/useSettings';
import { useSheetMusic } from '@/composables/useSheetMusic';
import { getSectionColors } from '@/constants';

const SECTION_COLORS = getSectionColors();
const settings = useSettings();
const sheetMusic = useSheetMusic();

const props = withDefaults(defineProps<{
  musicXmlFile: File;
  selectedId?: string | null;
  sectionPaddingY?: number;
  editable?: boolean;
  // Drops every overlay, leaving the bare score — sections aren't even drawn.
  readOnly?: boolean;
  // False while pre-rendered hidden; defers focus so it isn't stolen from behind.
  active?: boolean;
}>(), {
  selectedId: null,
  sectionPaddingY: 6,
  editable: false,
  readOnly: false,
  active: true,
});

const sections = defineModel<PieceSection[]>('sections', { default: () => [] });

const emit = defineEmits<{
  'section-click': [id: string];
  'section-activate': [section: PieceSection, event: MouseEvent];
  'container-click': [];
  'keydown': [event: KeyboardEvent];
  'section-edit-start': [];
  'section-edit-end': [];
}>();

const rootEl = ref<HTMLElement>();
const containerEl = ref<HTMLElement>();
const osmdEl = ref<HTMLElement>();
const isLoading = ref(true);
const measureBounds = ref<MeasureBound[]>([]);

// Zoom (persisted via useSettings) re-renders OSMD on change; see applyZoom.
// Step matches the OSMD demo's zoom buttons (×/÷ 1.2 per click).
const ZOOM_STEP = 1.2;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 3;
const zoom = settings.sectionsEditorZoom;
const zoomPercent = computed(() => Math.round(zoom.value * 100));
const canZoomIn = computed(() => zoom.value < ZOOM_MAX);
const canZoomOut = computed(() => zoom.value > ZOOM_MIN);

onMounted(async () => {
  await nextTick();
  if (props.active) rootEl.value?.focus();
  await renderSheet();
});

// Pre-rendered hidden, then shown: take focus on that transition, not at mount.
watch(() => props.active, (active) => {
  if (!active) return;
  rootEl.value?.focus();
  scrollSelectedSectionIntoView();
});

onBeforeUnmount(() => {
  sheetMusic.osmd.value?.clear();
});

async function renderSheet() {
  if (!osmdEl.value) return;
  isLoading.value = true;
  try {
    await sheetMusic.initOSMD(
      osmdEl.value,
      { renderSingleHorizontalStaffline: false },
    );
    await sheetMusic.renderMusicXml(await readMusicXmlFile(props.musicXmlFile), zoom.value);
    recolorOsmdByTheme(osmdEl.value, settings.currentTheme.value);
    await nextTick();
    recomputeMeasureBounds();
    scrollSelectedSectionIntoView();
  } catch (e) {
    console.error('[SheetMusicSectionsEditor] OSMD render error', e);
  } finally {
    isLoading.value = false;
  }
}

function recomputeMeasureBounds() {
  measureBounds.value = computeOsmdMeasureBounds({
    osmd: sheetMusic.osmd.value,
    osmdEl: osmdEl.value,
    includeVertical: true,
    includeRowGrouping: true,
    sectionPaddingY: props.sectionPaddingY,
  });
}

// Re-render the existing OSMD instance at the current zoom (no XML reload), then
// realign overlays — measure/section positions derive from osmd.zoom.
async function applyZoom() {
  if (!sheetMusic.osmd.value) return;
  try {
    sheetMusic.renderOsmd(zoom.value);
    recolorOsmdByTheme(osmdEl.value, settings.currentTheme.value);
    await nextTick();
    recomputeMeasureBounds();
  } catch (e) {
    console.error('[SheetMusicSectionsEditor] OSMD zoom re-render error', e);
  }
}

function setZoom(next: number) {
  const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, roundToTwoDecimals(next)));
  if (clamped !== zoom.value) zoom.value = clamped;
}

function zoomIn() {
  setZoom(zoom.value * ZOOM_STEP);
}

function zoomOut() {
  setZoom(zoom.value / ZOOM_STEP);
}

watch(zoom, () => { applyZoom(); });

function measureStyle(mb: MeasureBound) {
  return {
    left: `${mb.x}px`,
    top: `${mb.y}px`,
    width: `${mb.width}px`,
    height: `${mb.height}px`,
    zIndex: 10,
  };
}

function segmentStyle(seg: SectionSegment, section: PieceSection) {
  const isSelected = section.id === props.selectedId;
  return {
    left: `${seg.x}px`,
    top: `${seg.y}px`,
    width: `${seg.width}px`,
    height: `${seg.height}px`,
    backgroundColor: hexToRgba(section.color, isSelected ? 0.55 : 0.38),
    zIndex: 20,
    cursor: props.editable ? 'grab' : 'pointer',
  };
}

function sectionLabelStyle(seg: SectionSegment) {
  const scrollLeft = containerEl.value?.scrollLeft ?? 0;
  return {
    left: `${Math.max(0, scrollLeft - seg.x) + 8}px`,
    textShadow: '0 1px 3px rgba(0,0,0,0.7)',
    maxWidth: '140px',
  };
}

function getSectionSegments(section: PieceSection): SectionSegment[] {
  return computeSectionSegments(section, measureBounds.value);
}

// The sheet opens wherever it was last scrolled to, which is rarely the section being
// looped. Positioned from measureBounds rather than scrollIntoView(): the overlays are
// still out of the DOM on the first render, and one call cannot express "hold still if
// already visible, otherwise centre".
function scrollSelectedSectionIntoView() {
  const container = containerEl.value;
  const section = sections.value.find((s) => s.id === props.selectedId);
  if (!container || !section) return;

  const [firstSegment] = getSectionSegments(section);
  if (!firstSegment) return;

  container.scrollTop = alignScroll(container.scrollTop, container.clientHeight, firstSegment.y, firstSegment.height);
  container.scrollLeft = alignScroll(container.scrollLeft, container.clientWidth, firstSegment.x, firstSegment.width);
}

// Already fully in view: hold still, so selecting a section that is on screen never jumps
// the score. Otherwise centre it, or pin its start when it outgrows the viewport.
function alignScroll(scroll: number, viewport: number, start: number, length: number) {
  if (start >= scroll && start + length <= scroll + viewport) return scroll;
  return Math.max(0, start - Math.max(0, (viewport - length) / 2));
}

// Never mid-gesture: drag-creating a section selects it, and scrolling then would pull the
// score out from under the finger.
watch(() => props.selectedId, () => {
  if (!isGesturing) scrollSelectedSectionIntoView();
});

function getMeasureAtClientPos(clientX: number, clientY: number): MeasureBound | null {
  if (!containerEl.value) return null;
  return getMeasureAtPoint(
    measureBounds.value,
    containerEl.value.getBoundingClientRect(),
    clientX,
    clientY,
    {
      scrollLeft: containerEl.value.scrollLeft,
      scrollTop: containerEl.value.scrollTop,
    }
  );
}

function createSection(startMeasureIndex: number) {
  const newSection: PieceSection = {
    id: crypto.randomUUID(),
    name: 'New Section',
    color: SECTION_COLORS[sections.value.length % SECTION_COLORS.length],
    startMeasureIndex,
    endMeasureIndex: startMeasureIndex,
  };
  sections.value.push(newSection);
  emit('section-click', newSection.id);
  return newSection.id;
}

function updateSectionMeasures(id: string, startMeasureIndex: number, endMeasureIndex: number) {
  const section = sections.value.find((s) => s.id === id);
  if (!section) return;
  section.startMeasureIndex = startMeasureIndex;
  section.endMeasureIndex = endMeasureIndex;
}

function wouldOverlap(startMeasure: number, endMeasure: number, excludeSectionId: string) {
  return sections.value.some(
    (s) => s.id !== excludeSectionId && !(endMeasure < s.startMeasureIndex || startMeasure > s.endMeasureIndex)
  );
}

// True for the whole of a gesture that claimed the pointer.
let isGesturing = false;

function endPointerGesture(target: Element | null, pointerId: number) {
  isGesturing = false;
  target?.releasePointerCapture(pointerId);
  emit('section-edit-end');
}

function beginPointerDrag(
  event: PointerEvent,
  onMove: (ev: PointerEvent) => void,
) {
  const pointerId = event.pointerId;
  const target = event.currentTarget as Element | null;
  target?.setPointerCapture(pointerId);

  const handleMove = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    if (ev.pointerType === 'touch') ev.preventDefault();
    onMove(ev);
  };

  const handleEnd = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleEnd);
    window.removeEventListener('pointercancel', handleEnd);
    endPointerGesture(target, pointerId);
  };

  isGesturing = true;
  emit('section-edit-start');
  window.addEventListener('pointermove', handleMove, { passive: false });
  window.addEventListener('pointerup', handleEnd);
  window.addEventListener('pointercancel', handleEnd);
}

// Horizontal motion past this many px claims the pointer; below it, `touch-action: pan-y` lets the browser scroll.
const HORIZONTAL_CLAIM_THRESHOLD = 8;

function beginGatedPointerDrag(
  event: PointerEvent,
  options: {
    // Omitted when there is nothing to drag: the gesture can then only end as a tap, and
    // must never claim the pointer — that would block the browser's own scrolling.
    onMove?: (ev: PointerEvent) => void;
    onClaim?: (ev: PointerEvent) => void;
    onTap?: () => void;
  },
) {
  const { onMove, onClaim, onTap } = options;
  const pointerId = event.pointerId;
  const target = event.currentTarget as Element | null;
  const start = toLogicalPoint(event.clientX, event.clientY);
  let claimed = false;

  const cleanup = () => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    window.removeEventListener('pointercancel', handleCancel);
  };

  const handleMove = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId || !onMove) return;
    if (!claimed) {
      // Compare travel along the sheet's own axes, not the screen's — they differ
      // while the app is rotated for mobile portrait.
      const point = toLogicalPoint(ev.clientX, ev.clientY);
      const adx = Math.abs(point.x - start.x);
      const ady = Math.abs(point.y - start.y);
      if (adx <= HORIZONTAL_CLAIM_THRESHOLD || adx <= ady) return;
      claimed = true;
      isGesturing = true;
      target?.setPointerCapture(pointerId);
      emit('section-edit-start');
      onClaim?.(ev);
    }
    if (ev.pointerType === 'touch' && ev.cancelable) ev.preventDefault();
    onMove(ev);
  };

  const handleUp = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    cleanup();
    if (claimed) {
      endPointerGesture(target, pointerId);
    } else {
      onTap?.();
    }
  };

  const handleCancel = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    cleanup();
    if (claimed) endPointerGesture(target, pointerId);
    // If never claimed, the browser took over (e.g. for native scroll); do nothing.
  };

  window.addEventListener('pointermove', handleMove, { passive: false });
  window.addEventListener('pointerup', handleUp);
  window.addEventListener('pointercancel', handleCancel);
}

function startDragCreatePointer(startMeasureIndex: number, event: PointerEvent) {
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  let newId: string | null = null;
  beginGatedPointerDrag(event, {
    onClaim: () => {
      newId = createSection(startMeasureIndex);
    },
    onMove: (ev) => {
      if (!newId) return;
      const mb = getMeasureAtClientPos(ev.clientX, ev.clientY);
      if (!mb) return;
      const start = Math.min(startMeasureIndex, mb.measureIndex);
      const end = Math.max(startMeasureIndex, mb.measureIndex);
      if (wouldOverlap(start, end, newId)) return;
      updateSectionMeasures(newId, start, end);
    },
    onTap: () => createSection(startMeasureIndex),
  });
}

function startDragEdgePointer(section: PieceSection, edge: 'head' | 'tail', event: PointerEvent) {
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  beginPointerDrag(event, (ev) => {
    const mb = getMeasureAtClientPos(ev.clientX, ev.clientY);
    if (!mb) return;
    const newStart = edge === 'head' ? mb.measureIndex : section.startMeasureIndex;
    const newEnd = edge === 'tail' ? mb.measureIndex : section.endMeasureIndex;
    if (newStart > newEnd) return;
    if (wouldOverlap(newStart, newEnd, section.id)) return;
    updateSectionMeasures(section.id, newStart, newEnd);
  });
}

// A section can only be dragged if editing is on and the press landed on a measure inside
// it. With no grab point the gesture carries no onMove, so it can only end as a tap.
function sectionMoveHandler(section: PieceSection, event: PointerEvent) {
  if (!props.editable) return;
  const grabbed = getMeasureAtClientPos(event.clientX, event.clientY);
  if (!grabbed || grabbed.measureIndex < section.startMeasureIndex || grabbed.measureIndex > section.endMeasureIndex) return;

  const offsetFromStart = grabbed.measureIndex - section.startMeasureIndex;
  const sectionLength = section.endMeasureIndex - section.startMeasureIndex;

  return (ev: PointerEvent) => {
    const mb = getMeasureAtClientPos(ev.clientX, ev.clientY);
    if (!mb) return;
    const newStart = mb.measureIndex - offsetFromStart;
    const newEnd = newStart + sectionLength;
    if (newStart < 0) return;
    if (wouldOverlap(newStart, newEnd, section.id)) return;
    updateSectionMeasures(section.id, newStart, newEnd);
  };
}

// Tap selects, horizontal drag moves the section. Selection rides the gesture rather than
// `click`: a click is dispatched at pointerup and hit-tests the DOM as it is *then*, so a
// section just created — or carried under the finger — would land that click on itself.
function startSectionPointer(section: PieceSection, event: PointerEvent) {
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  beginGatedPointerDrag(event, {
    onTap: () => emit('section-click', section.id),
    onMove: sectionMoveHandler(section, event),
  });
}
</script>
